<template>
    <div class="login">
        <!-- 背景旋转光环，纯装饰，营造科幻入口感 -->
        <div class="aura aura-1"></div>
        <div class="aura aura-2"></div>
        <div class="aura aura-3"></div>
        <!-- 缓慢旋转的光束扇形，强化"能量入口"氛围 -->
        <div class="rays"></div>

        <TiltCard class="login-card" :max="6" :disabled="isTouch">
            <div class="lc-beam"></div>

            <div class="lc-brand">
                <div class="lc-orb"></div>
                <div>
                    <div class="lc-title grad-text">A-UNICORN</div>
                    <div class="lc-sub muted2">KEY SYSTEM · CONSOLE ACCESS</div>
                </div>
            </div>

            <h1 class="lc-h1">控制台登录</h1>
            <p class="lc-desc muted">输入 <code>admin_token</code> 验证身份后进入管理后台。</p>

            <div class="field">
                <div class="cap">ADMIN TOKEN</div>
                <input class="input mono" v-model="token" type="password" placeholder="请输入 admin_token"
                    @keyup.enter="save" autofocus />
            </div>

            <div v-if="err" class="err">{{ err }}</div>

            <div class="actions">
                <button class="btn btn-primary lc-go" @click="save">
                    进入控制台 <span class="arrow">→</span>
                </button>
                <button class="btn" @click="clear">清空</button>
            </div>

        </TiltCard>
    </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../stores/auth";
import TiltCard from "../components/TiltCard.vue";

const router = useRouter();
const auth = useAuthStore();

const token = ref(auth.adminToken || "");
const err = ref("");

// 触屏设备关闭 3D 倾斜，避免误触时卡片乱晃
const isTouch = typeof window !== "undefined" && "ontouchstart" in window;

function save() {
    err.value = "";
    if (!token.value.trim()) {
        err.value = "admin_token 不能为空";
        return;
    }
    auth.setToken(token.value);
    router.push("/keys");
}

function clear() {
    auth.logout();
    token.value = "";
    err.value = "";
}
</script>

<style scoped>
.login {
    position: relative;
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 24px;
    overflow: hidden;
}

/* 旋转光环 */
.aura {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    opacity: .5;
    pointer-events: none;
}

.aura-1 {
    width: 520px;
    height: 520px;
    background: radial-gradient(circle, rgba(124, 92, 255, .55), transparent 65%);
    top: -120px;
    left: -80px;
    animation: drift 14s ease-in-out infinite alternate;
}

.aura-2 {
    width: 460px;
    height: 460px;
    background: radial-gradient(circle, rgba(0, 229, 255, .45), transparent 65%);
    bottom: -120px;
    right: -60px;
    animation: drift 18s ease-in-out infinite alternate-reverse;
}

.aura-3 {
    width: 380px;
    height: 380px;
    background: radial-gradient(circle, rgba(0, 255, 168, .32), transparent 65%);
    top: 40%;
    left: 60%;
    animation: drift 22s ease-in-out infinite alternate;
}

/* 缓慢旋转的锥形光束：用 conic-gradient 做一圈扫光，柔化后像能量场 */
.rays {
    position: absolute;
    width: 140vmax;
    height: 140vmax;
    left: 50%;
    top: 50%;
    margin-left: -70vmax;
    margin-top: -70vmax;
    pointer-events: none;
    opacity: .18;
    background: conic-gradient(from 0deg,
            transparent 0deg, rgba(124, 92, 255, .5) 20deg, transparent 40deg,
            transparent 120deg, rgba(0, 229, 255, .45) 140deg, transparent 160deg,
            transparent 240deg, rgba(0, 255, 168, .4) 260deg, transparent 280deg);
    mask-image: radial-gradient(circle, #000 0%, transparent 62%);
    -webkit-mask-image: radial-gradient(circle, #000 0%, transparent 62%);
    animation: spin 40s linear infinite;
}

.login-card {
    width: min(440px, 100%);
    padding: 26px;
    animation: float-in .6s cubic-bezier(.22, 1, .36, 1) both;
}

.lc-beam {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, transparent, #7c5cff, #00e5ff, #00ffa8, transparent);
    background-size: 200% 100%;
    animation: beam-flow 4s linear infinite;
    box-shadow: 0 0 12px rgba(0, 229, 255, .6);
}

@keyframes beam-flow {
    to {
        background-position: -200% 0;
    }
}

.lc-brand {
    display: flex;
    align-items: center;
    gap: 12px;
}

.lc-orb {
    width: 42px;
    height: 42px;
    border-radius: 13px;
    background: var(--grad-main);
    box-shadow: var(--glow-violet);
    position: relative;
    animation: spin 8s linear infinite;
}

.lc-orb::before {
    content: "";
    position: absolute;
    inset: -8px;
    border-radius: 18px;
    background: var(--grad-main);
    filter: blur(12px);
    opacity: .55;
    z-index: -1;
    animation: orb-glow 3.2s ease-in-out infinite;
}

@keyframes orb-glow {

    0%,
    100% {
        opacity: .4;
        transform: scale(.9);
    }

    50% {
        opacity: .8;
        transform: scale(1.1);
    }
}

.lc-orb::after {
    content: "";
    position: absolute;
    inset: 6px;
    border-radius: 8px;
    background: var(--bg-1);
}

.lc-title {
    font-weight: 900;
    font-size: 18px;
    letter-spacing: .08em;
}

.lc-sub {
    font-size: 10px;
    letter-spacing: .12em;
    margin-top: 2px;
}

.lc-h1 {
    margin: 22px 0 6px;
    font-size: 24px;
    font-weight: 800;
}

.lc-desc {
    margin: 0 0 18px;
    font-size: 13px;
}

.field {
    margin-bottom: 14px;
    transition: transform var(--t);
}

/* 输入聚焦时整组轻微抬升 + cap 标签变青，引导视线 */
.field:focus-within {
    transform: translateY(-2px);
}

.err {
    margin-bottom: 14px;
    padding: 10px 13px;
    border-radius: var(--r-sm);
    border: 1px solid rgba(255, 77, 141, .4);
    background: rgba(255, 77, 141, .1);
    color: #ffc2d6;
    font-size: 13px;
}

.actions {
    display: flex;
    gap: 10px;
}

.lc-go {
    flex: 1 1 auto;
}

.arrow {
    transition: transform var(--t);
}

.lc-go:hover .arrow {
    transform: translateX(4px);
}

@keyframes drift {
    from {
        transform: translate(0, 0) scale(1);
    }

    to {
        transform: translate(40px, 30px) scale(1.12);
    }
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 768px) {
    .login-card {
        padding: 20px;
    }

    .lc-h1 {
        font-size: 21px;
    }
}
</style>
